import { motion } from "framer-motion";
import { RevealLines } from "@/components/RevealText";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { Marquee } from "@/components/Marquee";
import { EnquiryForm } from "@/components/EnquiryForm";
import { USPS } from "@/data/site";
import { Plus } from "lucide-react";

const IMG = "https://images.unsplash.com/photo-1614595737476-42487331b8a1?crop=entropy&cs=srgb&fm=jpg&w=2000&q=85";

const About = () => (
    <div data-testid="about-page">
        <section className="pt-40 pb-24 border-b border-white/10">
            <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
                <Breadcrumbs items={[{ label: "About", to: "/about" }]} />
                <div className="mt-10 flex items-center gap-4">
                    <div className="h-px w-12 bg-edge" />
                    <span className="text-[10px] tracking-[0.35em] text-edge uppercase font-bold">About Thermo Edge</span>
                </div>
                <RevealLines
                    as="h1"
                    lines={["The dealer that", <>installs like a <span className="text-edge">contractor</span>.</>]}
                    className="mt-8 font-display font-black uppercase tracking-tighter text-[12vw] md:text-[9vw] lg:text-[7vw] leading-[0.9]"
                />
            </div>
        </section>

        <section className="py-24 border-b border-white/10">
            <div className="max-w-[1600px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12">
                <div className="lg:col-span-5 relative overflow-hidden aspect-[4/5]">
                    <motion.img
                        initial={{ scale: 1.15 }}
                        whileInView={{ scale: 1 }}
                        transition={{ duration: 1.6, ease: [0.65, 0, 0.35, 1] }}
                        viewport={{ once: true }}
                        src={IMG}
                        alt="Modern building"
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-ink-950/60 to-transparent" />
                </div>
                <div className="lg:col-span-6 lg:col-start-7 space-y-10">
                    <div>
                        <div className="font-editorial italic text-edge text-4xl">01</div>
                        <h2 className="font-display uppercase text-3xl md:text-4xl font-black mt-3 tracking-tight">Overview</h2>
                        <p className="mt-6 text-white/75 leading-relaxed text-lg">
                            Thermo Edge Insulation is an authorized dealer and solutions provider of premium insulation products across Bihar, extending pan-India on request. We supply, specify, and install — and we don't hand off the last mile.
                        </p>
                    </div>
                    <div>
                        <div className="font-editorial italic text-edge text-4xl">02</div>
                        <h2 className="font-display uppercase text-3xl md:text-4xl font-black mt-3 tracking-tight">Mission</h2>
                        <p className="mt-6 text-white/75 leading-relaxed text-lg">
                            Cool every industrial roof we touch by at least 8°C — and prove it with a temperature reading before we leave the site.
                        </p>
                    </div>
                    <div>
                        <div className="font-editorial italic text-edge text-4xl">03</div>
                        <h2 className="font-display uppercase text-3xl md:text-4xl font-black mt-3 tracking-tight">Vision</h2>
                        <p className="mt-6 text-white/75 leading-relaxed text-lg">
                            To be the insulation partner that Indian warehouse owners, poultry farmers and factory managers call first — because we specify honestly, install cleanly, and answer the phone.
                        </p>
                    </div>
                </div>
            </div>
        </section>

        <Marquee items={["Warehouses", "Factories", "Poultry Farms", "Cold Storage", "Logistics", "Commercial"]} speed="slow" />

        <section className="py-24 border-b border-white/10">
            <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
                <div className="flex items-center gap-4 mb-6">
                    <div className="h-px w-12 bg-edge" />
                    <span className="text-[10px] tracking-[0.35em] text-edge uppercase font-bold">Core Values</span>
                </div>
                <h2 className="font-display uppercase text-4xl md:text-5xl lg:text-6xl font-black tracking-tighter">
                    What we won't compromise on.
                </h2>
                <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-5 gap-x-8 gap-y-6">
                    {USPS.map((usp) => (
                        <div key={usp} className="flex items-start gap-3 border-t border-white/10 pt-6">
                            <Plus className="w-4 h-4 text-edge mt-1 shrink-0" strokeWidth={2.5} />
                            <span className="font-display font-semibold uppercase tracking-tight">{usp}</span>
                        </div>
                    ))}
                </div>
            </div>
        </section>

        <section className="py-24 border-b border-white/10" data-testid="about-cta">
            <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12">
                <div className="lg:col-span-5">
                    <h2 className="font-display uppercase text-4xl md:text-5xl font-black tracking-tighter leading-[0.95]">
                        Let's talk about your building.
                    </h2>
                    <p className="mt-6 text-white/70">Send a quick note and we'll call you back today.</p>
                </div>
                <div className="lg:col-span-6 lg:col-start-7 border border-white/10 bg-ink-900/50 p-8 lg:p-10">
                    <EnquiryForm variant="full" source="about-page" />
                </div>
            </div>
        </section>
    </div>
);

export default About;